import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Package,
  ChevronDown,
  Copy,
  Check,
  Trash2,
  ExternalLink,
  Tag,
  Calendar,
  RefreshCw,
} from 'lucide-react';

interface PackageVersion {
  version: string;
  created_at: string;
  html_url: string;
}

interface GroupedPackage {
  name: string;
  html_url: string;
  repository: string | null;
  versions: PackageVersion[];
}

export default function Registry() {
  const [packages, setPackages] = useState<GroupedPackage[]>([]);
  const [registryHost, setRegistryHost] = useState('');
  const [owner, setOwner] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedPackage, setExpandedPackage] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ packageName: string; version: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/forgejo/packages', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPackages(data.data.packages || []);
        setRegistryHost(data.data.registryHost || '');
        setOwner(data.data.owner || '');
      } else {
        const data = await response.json().catch(() => ({ error: 'Failed to load registry packages' }));
        setPackages([]);
        setRegistryHost('');
        setOwner('');
        toast.error(data.error || 'Failed to load registry packages');
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load registry packages');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (packageName: string) => {
    setExpandedPackage(expandedPackage === packageName ? null : packageName);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await fetch(
        `/api/forgejo/packages/version?name=${encodeURIComponent(deleteTarget.packageName)}&version=${encodeURIComponent(deleteTarget.version)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (response.ok) {
        toast.success('Image version deleted');
        await fetchPackages();
      } else {
        const data = await response.json().catch(() => ({ error: 'Failed to delete image version' }));
        toast.error(data.error || 'Failed to delete image version');
      }
    } catch (error) {
      console.error('Failed to delete version:', error);
      toast.error('Failed to delete image version');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Pull command copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Clipboard API may fail in insecure contexts
    }
  };

  const getPullCommand = (packageName: string, tag: string) => {
    return `docker pull ${registryHost}/${owner}/${packageName}:${tag}`;
  };

  const isTagVersion = (version: string) => {
    return !version.startsWith('sha256:') && version !== 'buildcache';
  };

  if (loading) {
    return (
      <div className="space-y-6">
          <PageHeader title="Registry" description="Container images built from your projects" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border rounded-lg p-4 space-y-3">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        </div>
    );
  }

  return (
    <div className="space-y-6">
        <PageHeader
          title="Registry"
          description="Container images built from your projects"
          action={
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => {
                fetchPackages();
                setExpandedPackage(null);
              }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          }
        />

        {packages.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No container images"
            description="Images will appear here after your first CI/CD build"
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg) => {
              const tagVersions = pkg.versions.filter(v => isTagVersion(v.version));
              const isExpanded = expandedPackage === pkg.name;

              return (
                <div
                  key={pkg.name}
                  className="bg-card border rounded-lg p-4 cursor-pointer hover:bg-accent/50 transition-colors duration-150"
                  onClick={() => toggleExpand(pkg.name)}
                >
                  {/* Package Header */}
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold truncate">{pkg.name}</h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {tagVersions.length} tag{tagVersions.length !== 1 ? 's' : ''}
                      </Badge>
                      {pkg.html_url && (
                        <a
                          href={pkg.html_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label="Open package in Forgejo"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <ChevronDown
                        className={`w-4 h-4 text-muted-foreground transition-transform duration-150 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono truncate mb-2">
                    {registryHost}/{owner}/{pkg.name}
                  </p>

                  {/* Expanded Version List */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                      {tagVersions.length > 0 ? (
                        tagVersions.map((version) => {
                          const pullCmd = getPullCommand(pkg.name, version.version);
                          const copyKey = `${pkg.name}-${version.version}`;

                          return (
                            <div
                              key={version.version}
                              className="flex items-center justify-between gap-2 p-2 rounded border bg-background"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <Tag className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  <span className="font-mono text-sm truncate">{version.version}</span>
                                </div>
                                <div className="flex items-center gap-1 mt-0.5 ml-5 text-xs text-muted-foreground">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(version.created_at).toLocaleDateString()}
                                </div>
                                <p className="font-mono text-[13px] text-muted-foreground truncate mt-1 ml-5">
                                  {pullCmd}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => copyToClipboard(pullCmd, copyKey)}
                                  title="Copy pull command"
                                >
                                  {copiedField === copyKey ? (
                                    <Check className="w-3.5 h-3.5 text-success" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => setDeleteTarget({ packageName: pkg.name, version: version.version })}
                                  title="Delete version"
                                  aria-label="Delete version"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No tagged versions found
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Delete Confirmation */}
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title="Delete image version"
          description={`Are you sure you want to delete version ${deleteTarget?.version}? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          loading={deleting}
          variant="destructive"
        />
      </div>
  );
}
